import React, { useState, useEffect } from 'react';
import { AppProvider, Button, Card, ContextualSaveBar, Frame, Page, Toast, Icon } from '@shopify/polaris';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { DuplicateMinor } from '@shopify/polaris-icons';
import Loader from 'react-loader-spinner';
import { ViewMajorMonotone } from '@shopify/polaris-icons';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { propOr, isEmpty, path } from 'ramda';
import { TitleBar } from '@shopify/app-bridge-react';
import Footer from '../components/Footer';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { CREATE_SHOP_CAREER_PORTAL, UPDATE_SHOP_CAREER_PORTAL } from '../apollo/mutation';

import CareerPortalRow from '../components/CareerPortalRow';

import { keysToCamel } from '../../utils/normalizer';

const CareerPortalIndex = ({ shopifyDomain, history, shopifyToken, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const [sectionsStatus, setSectionsStatus] = useState({});
  const [toggleStatus, setToggleStatus] = useState({});
  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [isLoading, setIsLoading] = useState(false);

  const careerPortal = propOr({}, 'careerPortal')(data);
  const shop = propOr({}, 'shop')(subscriptionData);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const isSubscribedUser = !isEmpty(currentSubscription);
  const isPremiumUser = isSubscribedUser && currentSubscription.subscriptionType === 'Premium';

  const setInitialStatus = () => {
    setSectionsStatus({
      deiStatment: careerPortal.deiStatment,
      departmentsSection: careerPortal.departmentsSection,
      employeeTestimonial: careerPortal.employeeTestimonial,
      introductionParagraph: careerPortal.introductionParagraph,
      benefitsSection: careerPortal.benefitsSection,
      talentCommunitySection: careerPortal.talentCommunitySection,
      sectionHero: careerPortal.sectionHero,
    });

    setToggleStatus({
      sectionHero: !!careerPortal.headingText,
      deiStatment: !!careerPortal.deiStatmentBody,
      departmentsSection: !isEmpty(careerPortal.departments),
      employeeTestimonial: !isEmpty(careerPortal.testimonials),
      introductionParagraph: !!careerPortal.introductionBody && !!careerPortal.introductionHeading,
      benefitsSection: !isEmpty(careerPortal.benefits),
    });
  };

  useEffect(() => {
    setInitialStatus();
  }, [data]);

  const filterPageTimeLabel = (label) => {
    return careerPortal.pageTimestamps.filter((val) => val.attribute == label)[0].label
  }

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    setIsLoading(false);
    refetch();
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  const onToggleActive = (id) => {
    setIsLoading(true);
    const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
    const { styleControls, ...careerPortalFormatted } = careerPortal;
    const styleControlsFormatted = keysToCamel(styleControls);

    const { talentCommunitySection: talentCommunityStyles } = { ...styleControlsFormatted };
    const variables = {
      ...careerPortalFormatted,
      ...styleControlsFormatted,
      talentCommunityStyles,
      ...{ ...sectionsStatus, [id]: !sectionsStatus[id] },
      shopifyToken,
    };

    save({ variables }).then(onSuccess).catch(onError);
  };

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  return (
    <AppProvider
      i18n={{
        Polaris: {
          ContextualSaveBar: {
            save: 'Save',
            discard: 'Discard',
          },
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <TitleBar title='Career Portal' />
        <Page
          title='Career Portal Setup'
          subtitle={
            <Button
              external
              plain
              monochrome
              outline={false}
              icon={ViewMajorMonotone}
              url={careerPageLink}>
              View career portal
            </Button>
          }>
          <Card title='CAREERS PAGE'>
            <Card.Section>
              <CopyToClipboard
                text={careerPageLink}
                onCopy={() => {
                  setToast({
                    isOpen: true,
                    text: 'Copied to clipboard',
                  });
                }}>
                <Button monochrome icon={DuplicateMinor} size={'slim'}>
                  {careerPageLink}
                </Button>
              </CopyToClipboard>
            </Card.Section>
            <CareerPortalRow
              id='sectionHero'
              title='Hero section'
              subtitle= {filterPageTimeLabel("section_hero")}
              onEdit={() => changeRoute('/career-portal/hero-section')}
              isActive={sectionsStatus.sectionHero}
              onToggleActive={toggleStatus.sectionHero && onToggleActive}
            />
            <CareerPortalRow
              id='introductionParagraph'
              title='Intro paragraph section'
              subtitle={filterPageTimeLabel("introduction_paragraph")}
              onEdit={() => changeRoute('/career-portal/intro-paragraph-section')}
              isActive={sectionsStatus.introductionParagraph}
              onToggleActive={toggleStatus.introductionParagraph && onToggleActive}
            />
            <CareerPortalRow
              id='employeeTestimonial'
              title='Employee testimonial section'
              subtitle={filterPageTimeLabel("employee_testimonial")}
              isActive={sectionsStatus.employeeTestimonial}
              changeRoute={changeRoute}
              onEdit={() => changeRoute('/career-portal/employee-testimonials-section')}
              onToggleActive={toggleStatus.employeeTestimonial && onToggleActive}
            />
            <CareerPortalRow
              id='deiStatment'
              title='Diversity statement section'
              subtitle={filterPageTimeLabel("dei_statment")}
              isActive={sectionsStatus.deiStatment}
              onEdit={() => changeRoute('/career-portal/diversity-statement-section')}
              onToggleActive={toggleStatus.deiStatment && onToggleActive}
            />
            <CareerPortalRow
              id='departmentsSection'
              title='Departments section'
              subtitle={filterPageTimeLabel("departments_section")}
              isActive={sectionsStatus.departmentsSection}
              changeRoute={changeRoute}
              onEdit={() => changeRoute('/career-portal/departments-section')}
              onToggleActive={toggleStatus.departmentsSection && onToggleActive}
            />
            <CareerPortalRow
              id='benefitsSection'
              title='Perks & benefits section'
              subtitle={filterPageTimeLabel("benefits_section")}
              isActive={sectionsStatus.benefitsSection}
              onToggleActive={toggleStatus.benefitsSection && onToggleActive}
              changeRoute={changeRoute}
              onEdit={() => changeRoute('/career-portal/benefits-section')}
            />
            <CareerPortalRow
              id='job'
              title='Job listings section'
              subtitle=''
              onEdit={() => changeRoute('/career-portal/job-listings-section')}
              isActive
              isFreemium
            />
            <CareerPortalRow
              id='talentCommunitySection'
              title='Job alert sign up section'
              subtitle={filterPageTimeLabel("talent_community_section")}
              onEdit={() => changeRoute('/career-portal/talent-community-section')}
              changeRoute={changeRoute}
              isActive={sectionsStatus.talentCommunitySection}
              onToggleActive={onToggleActive}
            />
            <CareerPortalRow
              id='job-description'
              title='Job description page'
              subtitle=''
              onEdit={() => changeRoute('/career-portal/job-description-page')}
              isActive
              isFreemium
            />
            <CareerPortalRow
              id='post-apply'
              title='Post apply page'
              subtitle=''
              onEdit={() => changeRoute('/career-portal/post-apply-page')}
              changeRoute={changeRoute}
              isActive
              isFreemium
            />
          </Card>
        </Page>
        <Footer link='https://help.shophire.co/article/26-career-portal-overview' name='Career portal' />
        {toastMarkup}
      </Frame>
    </AppProvider>
  );
};

export default CareerPortalIndex;
